import { z } from "zod";
import { paginationQuery, searchParam } from "./pagination";

/**
 * `/countries` — the ISO-3166 reference list. Wire shapes only (snake_case),
 * as the API's `ok()` emits them after `snakeCaseKeys()`; see CLAUDE.md § Naming.
 *
 * The one thing to understand before touching this file: `iso_code` and
 * `ioc_code` are DIFFERENT CODES and are not interchangeable.
 *
 *   iso_code — ISO-3166 alpha-3. Every country has one. This is the identity;
 *              `people.country_code` points at it.
 *   ioc_code — the Olympic code, printed on the AC card. Only the competing
 *              nations have one, and 9 of the SEA Games 11 differ from their
 *              ISO code (MYS/MAS, SGP/SIN, IDN/INA, VNM/VIE…).
 *
 * Showing the ISO code where the IOC code belongs puts the wrong three letters
 * on a printed card. When in doubt, display the name.
 */

export const COUNTRY_SORT_KEYS = ["name", "iso_code", "ioc_code"] as const;
export type CountrySortKey = (typeof COUNTRY_SORT_KEYS)[number];

export const listCountriesQuery = paginationQuery.extend({
	search: searchParam.optional(),
	/** "true" → only countries with an IOC code, i.e. the ones competing. */
	competing: z.enum(["true", "false"]).optional(),
	sort: z.enum(COUNTRY_SORT_KEYS).optional(),
	dir: z.enum(["asc", "desc"]).optional(),
});
export type ListCountriesQuery = z.infer<typeof listCountriesQuery>;

/** One row of `GET /countries`. */
export interface CountryListItem {
	country_id: string;
	/** ISO-3166 alpha-3. Always present. */
	iso_code: string;
	/** Olympic code. Null for every country that is not competing. */
	ioc_code: string | null;
	name: string;
	/** Demonym — "Malaysian", "Filipino". This is what prints on the card. */
	nationality: string | null;
	/** Dial prefix with the plus — "+60". */
	calling_code: string | null;
	flag_url: string | null;
	/** People holding this nationality. A country in use cannot be renamed casually. */
	people_count: number;
	created_at: string;
	updated_at: string;
}

/** `GET /countries/stats` — the stats strip and the participation chips' counts, in one statement. Unfiltered. */
export interface CountryStats {
	total: number;
	/** Countries with an IOC code — the ones competing. */
	competing: number;
	/** Countries without one: kept for nationality, press and guests. */
	reference_only: number;
}

/** `GET /countries/options` — the whole list, for `<select>` elements. */
export interface CountryOption {
	country_id: string;
	iso_code: string;
	ioc_code: string | null;
	name: string;
	nationality: string | null;
	calling_code: string | null;
}

// ── Write requests ──────────────────────────────────────────────────────────
// Declared in snake_case to mirror `apps/api/src/schemas/country.schema.ts`,
// so the form validates with the same rules the server will apply.

/**
 * Every human-readable validation message the country form schemas emit, so a
 * frontend can hand in translated copy (`apps/gms/src/lib/i18n/use-form-schemas.ts`)
 * while the API and the default exports keep the English wire copy verbatim.
 */
export interface CountryFormMessages {
	codeExactThree: string;
	callingCodeFormat: string;
	urlInvalid: string;
	nameRequired: string;
}

/** The exact strings these schemas have always emitted — the English default. */
export const enCountryFormMessages: CountryFormMessages = {
	codeExactThree: "Exactly three letters",
	callingCodeFormat: "Like +60",
	urlInvalid: "Enter a valid URL",
	nameRequired: "Enter a name",
};

const code3 = (m: CountryFormMessages) =>
	z
		.string()
		.trim()
		.regex(/^[A-Za-z]{3}$/, m.codeExactThree)
		.transform((v) => v.toUpperCase());

/** Optional text inputs bind as `""`; the `to*Body` helpers drop or null them. */
const optionalCode3 = (m: CountryFormMessages) => code3(m).or(z.literal(""));
const optionalCallingCode = (m: CountryFormMessages) =>
	z
		.string()
		.trim()
		.regex(/^\+\d{1,6}$/, m.callingCodeFormat)
		.or(z.literal(""));
const optionalUrl = (m: CountryFormMessages) => z.string().trim().url(m.urlInvalid).max(2048).or(z.literal(""));

const countryFields = (m: CountryFormMessages) => ({
	name: z.string().trim().min(1, m.nameRequired).max(120),
	ioc_code: optionalCode3(m),
	nationality: z.string().trim().max(120),
	calling_code: optionalCallingCode(m),
	flag_url: optionalUrl(m),
});

export function makeCreateCountryForm(m: CountryFormMessages = enCountryFormMessages) {
	return z.object({
		iso_code: code3(m),
		...countryFields(m),
	});
}
export const createCountryForm = makeCreateCountryForm();
export type CreateCountryForm = z.infer<typeof createCountryForm>;

/**
 * `iso_code` is absent on purpose — it is the row's identity and `people` hangs
 * off it, so changing it would silently repoint every person of that
 * nationality. A wrong ISO code is a delete-and-recreate, not an edit.
 */
export function makeUpdateCountryForm(m: CountryFormMessages = enCountryFormMessages) {
	return z.object(countryFields(m));
}
export const updateCountryForm = makeUpdateCountryForm();
export type UpdateCountryForm = z.infer<typeof updateCountryForm>;

export interface CreateCountryBody {
	iso_code: string;
	name: string;
	ioc_code?: string;
	nationality?: string;
	calling_code?: string;
	flag_url?: string;
}

/** `null` clears a field; omitted leaves it alone. The two must stay distinguishable. */
export interface UpdateCountryBody {
	name?: string;
	ioc_code?: string | null;
	nationality?: string | null;
	calling_code?: string | null;
	flag_url?: string | null;
	/** Headline for this change in the Activity feed. */
	reason?: string;
}

export const toCreateCountryBody = (f: CreateCountryForm): CreateCountryBody => ({
	iso_code: f.iso_code,
	name: f.name,
	...(f.ioc_code ? { ioc_code: f.ioc_code } : {}),
	...(f.nationality ? { nationality: f.nationality } : {}),
	...(f.calling_code ? { calling_code: f.calling_code } : {}),
	...(f.flag_url ? { flag_url: f.flag_url } : {}),
});

/**
 * Only what actually changed, so the audit row's `changed_fields` names the
 * edit rather than listing every column. A field the admin cleared is sent as
 * an explicit `null`; one they left alone is omitted entirely.
 */
export function toUpdateCountryBody(f: UpdateCountryForm, before: CountryListItem): UpdateCountryBody {
	const body: UpdateCountryBody = {};
	if (f.name !== before.name) body.name = f.name;
	const diffNullable = (next: string, prev: string | null) => (next === "" ? (prev === null ? undefined : null) : next === prev ? undefined : next);

	const ioc = diffNullable(f.ioc_code, before.ioc_code);
	if (ioc !== undefined) body.ioc_code = ioc;
	const nat = diffNullable(f.nationality, before.nationality);
	if (nat !== undefined) body.nationality = nat;
	const call = diffNullable(f.calling_code, before.calling_code);
	if (call !== undefined) body.calling_code = call;
	const flag = diffNullable(f.flag_url, before.flag_url);
	if (flag !== undefined) body.flag_url = flag;
	return body;
}
